import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Paragraph from '../../Paragraph/Paragraph';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import Button from '../../Button/Button';
import { AGREEMENT_TYPE_PAGES } from '../../../constants/agreementConstants';
import {
  removeFromLocalStorage,
  storeInLocalStorage,
} from '../../../util/helpers/localStorageHelper';
import { AGREEMENT_TYPE } from '../../../constants/localStorage';
import { submitAccountAgreements } from '../../../store/actions/account/accountActions';
import SectionLoader from '../../Loader/SectionLoader';
import useGtmHook from '../../../util/hooks/useGtmHook';
import { APPLICATION_TERMS_ACCEPTED } from '../../../constants/gtmEvents';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BoldText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${themeColors.colorInfoText};
`;

const Separator = styled.span`
  color: ${themeColors.colorInfoLine};
`;

const TermsBox = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${themeColors.colorTermsBoxBg};
  padding: 8px 8px;
  margin: 16px 16px;
  align-items: center;
  border-radius: ${variables.borderRadius.borderRadius};
`;

const TermsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

const TermContainer = styled.div`
  display: flex;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 95%;
  margin: 0 16px 16px 16px;
`;

const PrimaryUserTerms = ({
  termsArray,
  accountUid,
  onSuccess,
  userWithMultipleAccounts,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { gtmEvent } = useGtmHook();
  const [isLoading, setIsLoading] = useState(false);
  const termsArrayLastIndex = termsArray?.length - 1;

  const onClickTerm = (value) => {
    storeInLocalStorage(AGREEMENT_TYPE, value);
  };

  const onClickAccept = () => {
    gtmEvent(APPLICATION_TERMS_ACCEPTED, {
      eventLabel: 'registration',
      gtm: {
        uniqueEventId: 34,
      },
    });

    dispatch(
      submitAccountAgreements({
        accountUid,
        setIsLoading,
        onSuccess,
      }),
    );
    removeFromLocalStorage(AGREEMENT_TYPE);
  };

  const Terms = () => (
    <TermsContainer>
      {termsArray.map((term, index) => (
        <TermContainer onClick={() => onClickTerm(term.value)}>
          <Paragraph
            marginRight={index !== termsArrayLastIndex ? 16 : ''}
            marginLeft={index !== 0 ? 16 : ''}
          >
            <Link to={AGREEMENT_TYPE_PAGES[term.label]} target="_blank">
              <Paragraph
                bold
                textDecoration="underline"
                color={themeColors.colorLinkPrimary}
              >
                {term.label}
              </Paragraph>
            </Link>
          </Paragraph>
          {index !== termsArrayLastIndex && <Separator>|</Separator>}
        </TermContainer>
      ))}
    </TermsContainer>
  );
  return (
    <SectionLoader isLoading={isLoading}>
      <ContentContainer>
        <Paragraph
          bold
          color={themeColors.colorInfoTitle}
          fontSize={29}
          marginBottom={16}
        >
          {t('updatedTerms.primary.title')}
        </Paragraph>
        <Paragraph color={themeColors.colorInfoText}>
          {t('updatedTerms.primary.subtitle1')}
        </Paragraph>
        <Paragraph color={themeColors.colorInfoText}>
          {t('updatedTerms.primary.subtitle2')}
        </Paragraph>
        <TermsBox>
          <Paragraph color={themeColors.colorInfoText}>
            {t('updatedTerms.primary.terms')}
          </Paragraph>
          <Terms />
        </TermsBox>
        <Paragraph bold marginBottom={4}>
          {t('updatedTerms.primary.whatThisMeans')}
        </Paragraph>
        <Paragraph color={themeColors.colorInfoText} marginBottom={4}>
          {t('updatedTerms.primary.body1')}
        </Paragraph>
        <Paragraph color={themeColors.colorInfoText} marginBottom={4}>
          {t('updatedTerms.primary.body2')}
        </Paragraph>
        {userWithMultipleAccounts && (
          <TermsBox>
            <Paragraph color={themeColors.colorInfoText}>
              <BoldText>{t('common.important')}</BoldText>
              {t('updatedTerms.primary.importantBodyAll')}
            </Paragraph>
          </TermsBox>
        )}
        <ButtonContainer>
          <Button
            variant="primary"
            size="xl"
            textTransform="none"
            marginTop={16}
            marginBottom={16}
            marginRight={16}
            marginLeft={16}
            isWidthS
            onClick={onClickAccept}
          >
            {t('updatedTerms.primary.buttonText')}
          </Button>
        </ButtonContainer>
        <Paragraph color={themeColors.colorInfoText} marginBottom={16}>
          {t('updatedTerms.footer1')}
          <BoldText>{t('updatedTerms.footer2')}</BoldText>
          {t('updatedTerms.footer3')}
        </Paragraph>
      </ContentContainer>
    </SectionLoader>
  );
};

PrimaryUserTerms.propTypes = {
  termsArray: PropTypes.arrayOf({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  accountUid: PropTypes.string,
  onSuccess: PropTypes.func,
  userWithMultipleAccounts: PropTypes.bool,
};

export default PrimaryUserTerms;
